import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FiMapPin,
  FiMail,
  FiPhone,
  FiMessageSquare,
  FiSend,
  FiCheck,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import PageHeader from "../components/PageHeader";
import products from "../data/products";
import "./Contact.css";
import WhatsAppContact from "../components/WhatsAppContact";

function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    country: "",
    product: "",
    quantity: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError("");

    try {
      const res = await fetch("/api/sendEmail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        setSent(true);
        setSubmitting(false);

        setTimeout(() => {
          setSent(false);
          setForm({
            name: "",
            company: "",
            email: "",
            country: "",
            product: "",
            quantity: "",
            message: "",
          });
        }, 5000);
      } else {
        setError(
          data.error ||
            "We couldn't send your inquiry. Please try again, or email info@agronexustrading.in directly."
        );
        setSubmitting(false);
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        "Network error. Please check your connection and try again, or email info@agronexustrading.in directly."
      );
      setSubmitting(false);
    }
  };

  return (
    <motion.main
      className="page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageHeader
        eyebrow="Contact"
        title="Tell us what you need, <em>we'll take it from there.</em>"
        subtitle="Share your target product, market, and volume. Our team responds within one working day with samples, pricing, and a shipment plan."
      />

      <section className="section contact-section">
        <div className="container contact-grid">
          {/* Form */}
          <motion.div
            className="contact-form-wrap"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {sent ? (
              <motion.div
                className="contact-success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className="contact-success__icon">
                  <FiCheck />
                </div>
                <h3>Enquiry received.</h3>
                <p>
                  Thank you. Our team will be in touch within one working day
                  with samples and indicative pricing.
                </p>
              </motion.div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit}>
                <h2>Send an inquiry</h2>
                <p className="contact-form__lede">
                  All fields with <span>*</span> are required.
                </p>

                <div className="contact-form__row">
                  <label>
                    <span className="contact-form__label">Your name <span className="contact-form__required">*</span></span>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="e.g. Rahul Patel"
                    />
                  </label>
                  <label>
                    <span className="contact-form__label">Company</span>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Company name"
                    />
                  </label>
                </div>

                <div className="contact-form__row">
                  <label>
                    <span className="contact-form__label">Email <span className="contact-form__required">*</span></span>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                    />
                  </label>
                  <label>
                    <span className="contact-form__label">Country</span>
                    <input
                      type="text"
                      name="country"
                      value={form.country}
                      onChange={handleChange}
                      placeholder="e.g. UAE, Germany"
                    />
                  </label>
                </div>

                <div className="contact-form__row">
                  <label>
                    <span className="contact-form__label">Product of interest</span>
                    <select
                      name="product"
                      value={form.product}
                      onChange={handleChange}
                    >
                      <option value="">Select a product</option>
                      {products.map((p) => (
                        <option key={p.slug} value={p.name}>
                          {p.name} ({p.local})
                        </option>
                      ))}
                      <option value="Multiple / Mixed">Multiple / Mixed</option>
                      <option value="Logistics only">Logistics service only</option>
                    </select>
                  </label>
                  <label>
                    <span className="contact-form__label">Estimated quantity</span>
                    <input
                      type="text"
                      name="quantity"
                      value={form.quantity}
                      onChange={handleChange}
                      placeholder="e.g. 1 x 20ft container, 5 MT"
                    />
                  </label>
                </div>

                <label className="contact-form__full">
                  <span className="contact-form__label">Message <span className="contact-form__required">*</span></span>
                  <textarea
                    name="message"
                    rows="5"
                    required
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your requirement — grade, packing, target market, timeline..."
                  />
                </label>

                {error && (
                  <div
                    className="contact-form__error"
                    role="alert"
                    style={{
                      background: "#fdecea",
                      color: "#a5251c",
                      border: "1px solid #f5c2bd",
                      padding: "0.8rem 1rem",
                      borderRadius: "8px",
                      marginBottom: "1rem",
                      fontSize: "0.92rem",
                    }}
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary contact-form__submit"
                  disabled={submitting}
                  style={submitting ? { opacity: 0.7, cursor: "wait" } : {}}
                >
                  {submitting ? "Sending..." : "Send inquiry"} <FiSend />
                </button>
              </form>
            )}
          </motion.div>

          {/* Info */}
          <motion.aside
            className="contact-info"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="contact-info__card">
              <div className="contact-info__icon">
                <FiMapPin />
              </div>
              <h4>Head office</h4>
              <p>
                Agronexus Trading Pvt. Ltd. <br />
                Near Thakkar Petroleum, Mothla, <br />
                Naliya–Bhuj Road, Kutch, <br />
                Gujarat — 370650
              </p>
            </div>

            <div className="contact-info__card">
              <div className="contact-info__icon">
                <FiMail />
              </div>
              <h4>Email us</h4>
              <p>
                <a href="mailto:info@agronexus.in">
                  info@agronexustrading.in
                </a>
                <br />
                <a href="mailto:sales@agronexus.in">
                  sales@agronexustrading.in
                </a>
              </p>
            </div>

            <div className="contact-info__card">
              <div className="contact-info__icon">
                <FiPhone />
              </div>
              <h4>Call us</h4>
              <p>
                <strong>Ramchandra Bishnoi</strong>
                <br />
                <a href="tel:+919909729560">+91 99097 29560</a>
                <br />
                Mon–Sat · 10:00 to 9:00 IST
              </p>
            </div>

            <a
              href="https://wa.me/919909729560"
              target="_blank"
              rel="noreferrer"
              className="contact-info__whatsapp"
            >
              <FaWhatsapp />
              <div>
                <strong>Chat on WhatsApp</strong>
                <span>Fastest response</span>
              </div>
            </a>

            <div className="contact-info__hours">
              <FiMessageSquare />
              <span>Average response time: under 24 hours on working days.</span>
            </div>
          </motion.aside>
        </div>
      </section>

      <section className="section contact-whatsapp-section">
        <div className="container contact-whatsapp-container">
          <WhatsAppContact />
        </div>
      </section>
    </motion.main>
  );
}

export default Contact;
